import { traitCodePorId } from '@equiview/protocol';
import { explicarErroDeSincronia, explicarRejeicao, type ItemEmQuarentena, type OutboxItem } from '@equiview/sync';
import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useState } from 'react';
import { Botao, CabecalhoPagina, Dado, Etiqueta, Painel } from '../components/ui/index.js';
import { formatarValor, ROTULO_VISTA, traitPorCodigo } from '../dados/catalogo.js';
import { useFonte } from '../dados/contexto.js';
import { env } from '../lib/env.js';
import { arquivos, db, DEVICE_ID, engine, sincronizarAgora, store, useSincronizando } from '../lib/local.js';

const ENTIDADE: Record<string, string> = {
  stud_farm: 'Haras',
  person: 'Pessoa',
  animal: 'Animal',
  instrument: 'Instrumento',
  visit: 'Visita',
  assessment: 'Ficha do animal',
  measurement_session: 'Sessão de medição',
  measurement: 'Medida',
  animal_management: 'Peso e condição corporal',
  media: 'Foto ou vídeo',
  gait_trial: 'Passagem de marcha',
  gait_subjective: 'Avaliação da marcha',
  sensor_pack: 'Sensores',
  validation_override: 'Justificativa de aviso',
  media_analysis: 'Análise de foto ou vídeo',
  amendment: 'Emenda',
};

const quando = (ms: number | null) => {
  if (ms == null) return 'nunca';
  const minutos = Math.round((Date.now() - ms) / 60_000);
  if (minutos < 1) return 'agora há pouco';
  if (minutos < 60) return `há ${minutos} min`;
  if (minutos < 24 * 60) return `há ${Math.round(minutos / 60)} h`;
  return new Date(ms).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
};

const dataHora = (ms: number) => new Date(ms).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

const megabytes = (bytes: number) => `${(bytes / 1024 / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;

/** "Foto de perfil esquerdo · Estrela", a partir da linha de `media` e da ficha. */
async function descreverArquivo(mediaId: string): Promise<string> {
  const m = await db.table('media').get(mediaId);
  if (!m) return 'Arquivo';
  const vista = m.view ? ROTULO_VISTA[m.view as keyof typeof ROTULO_VISTA] : null;
  const tipo = m.kind === 'video' ? 'Vídeo' : 'Foto';
  const ficha = await db.table('assessment').get(String(m.assessment_id));
  const animal = ficha ? await db.table('animal').get(String(ficha.animal_id)) : undefined;
  return [vista ? `${tipo} · ${vista.toLowerCase()}` : tipo, animal?.name].filter(Boolean).join(' · ');
}

/** O que o item e, em uma linha: "Medida · Altura de cernelha 152,5 cm", "Ficha do animal · Estrela". */
async function descrever(item: OutboxItem): Promise<string> {
  const p = item.payload;
  const tipo = ENTIDADE[item.entity] ?? item.entity;
  if (item.op === 'delete') return `${tipo} · exclusão`;
  const nomeDoAnimal = async (fichaId: unknown) => {
    const ficha = fichaId ? await db.table('assessment').get(String(fichaId)) : undefined;
    const animal = ficha ? await db.table('animal').get(String(ficha.animal_id)) : undefined;
    return animal?.name ? String(animal.name) : null;
  };
  let detalhe: string | null = null;
  if (item.entity === 'measurement') {
    const code = traitCodePorId(String(p.trait_code_id ?? ''));
    const trait = code ? traitPorCodigo.get(code) : undefined;
    detalhe = trait ? `${trait.label} ${formatarValor(Number(p.value_num), trait)} ${trait.unit}` : null;
  } else if (item.entity === 'animal' || item.entity === 'stud_farm') {
    detalhe = p.name ? String(p.name) : null;
  } else if (item.entity === 'visit') {
    detalhe = p.visit_number ? `nº ${p.visit_number}` : null;
  } else if (item.entity === 'assessment' || item.entity === 'animal_management') {
    detalhe = await nomeDoAnimal(item.entity === 'assessment' ? item.entityId : p.assessment_id ?? item.entityId);
  }
  return detalhe ? `${tipo} · ${detalhe}` : tipo;
}

/** Diagnostico e reconciliacao do sync: o que falta enviar, o que o servidor recusou e o que fazer. */
export function Sincronizacao() {
  const fonte = useFonte();
  const rodando = useSincronizando();
  const [online, setOnline] = useState(navigator.onLine);
  const [persistente, setPersistente] = useState<boolean | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);

  useEffect(() => {
    const ligou = () => setOnline(true);
    const caiu = () => setOnline(false);
    window.addEventListener('online', ligou);
    window.addEventListener('offline', caiu);
    navigator.storage?.persisted?.().then(setPersistente, () => setPersistente(false));
    return () => {
      window.removeEventListener('online', ligou);
      window.removeEventListener('offline', caiu);
    };
  }, []);

  const estado = useLiveQuery(() => store.getSyncState(), []);
  const pendentes = useLiveQuery(() => db.outbox.count(), [], 0);
  const fila = useLiveQuery(
    async () => Promise.all((await db.outbox.orderBy('createdAt').limit(20).toArray()).map(async (i) => ({ i, texto: await descrever(i) }))),
    [],
    [],
  );
  const arquivosPendentes = useLiveQuery(
    async () => Promise.all((await arquivos.pendentes()).map(async (a) => ({ a, texto: await descreverArquivo(a.mediaId) }))),
    [],
    [],
  );
  const quarentena = useLiveQuery(
    async () => Promise.all((await store.listQuarantine()).map(async (q) => ({ q, texto: await descrever(q.item) }))),
    [],
    [],
  );

  const sincronizar = async () => {
    setMensagem(null);
    try {
      const r = await sincronizarAgora();
      setMensagem(
        r.errors.length
          ? 'A sincronização terminou com erro. Veja abaixo.'
          : `Pronto: ${r.pushed} enviado(s), ${r.pulled} recebido(s)${r.rejected ? `, ${r.rejected} recusado(s)` : ''}.`,
      );
    } catch (e) {
      setMensagem(e instanceof Error ? e.message : String(e));
    }
  };

  const erros = [...new Map((estado?.lastErrors ?? []).map((e) => [explicarErroDeSincronia(e).titulo, explicarErroDeSincronia(e)])).values()];

  return (
    <div className="space-y-6">
      <CabecalhoPagina
        titulo="Sincronização"
        meta={<span>O que ainda está só neste aparelho, e o que o servidor recusou.</span>}
        acoes={
          <Botao variante="principal" carregando={rodando} disabled={!online || fonte.ficticia} onClick={() => void sincronizar()}>
            Sincronizar agora
          </Botao>
        }
      />

      {fonte.ficticia && (
        <p className="border-l-2 border-aviso px-3 py-2 text-sm">Com dados fictícios nada é enviado ao servidor.</p>
      )}
      {mensagem && (
        <p role="status" className="border-l-2 border-destaque px-3 py-2 text-sm">
          {mensagem}
        </p>
      )}

      <Painel titulo="Situação deste aparelho">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
          <Dado rotulo="Conexão">{online ? 'Conectado' : 'Offline'}</Dado>
          <Dado rotulo="Última sincronização completa">{quando(estado?.lastSyncAt ?? null)}</Dado>
          <Dado rotulo="Registros por enviar">
            <span className={`numero ${pendentes > 0 ? 'text-aviso' : ''}`}>{pendentes}</span>
          </Dado>
          <Dado rotulo="Em quarentena">
            <span className={`numero ${quarentena.length > 0 ? 'text-erro' : ''}`}>{quarentena.length}</span>
          </Dado>
          <Dado rotulo="Armazenamento">{persistente == null ? '—' : persistente ? 'Garantido' : 'Sem garantia'}</Dado>
          <Dado rotulo="Aparelho">
            <span className="font-mono text-xs">{DEVICE_ID.slice(0, 8)}</span>
          </Dado>
          <Dado rotulo="Versão do app">
            <span className="font-mono text-xs">{env.appVersion}</span>
          </Dado>
        </dl>
      </Painel>

      {erros.length > 0 && (
        <Painel titulo="Última sincronização teve problema">
          <ul className="space-y-3">
            {erros.map((e) => (
              <li key={e.titulo} className="border-l-2 border-erro pl-3">
                <p className="text-sm font-medium">{e.titulo}</p>
                <p className="text-sm text-apagado">{e.explicacao}</p>
              </li>
            ))}
          </ul>
        </Painel>
      )}

      <Painel titulo={`Quarentena${quarentena.length ? ` · ${quarentena.length}` : ''}`}>
        {quarentena.length === 0 ? (
          <p className="text-sm text-apagado">Nada recusado pelo servidor.</p>
        ) : (
          <ul className="space-y-4">
            {quarentena.map(({ q, texto }) => (
              <ItemQuarentena key={q.id} q={q} texto={texto} online={online} />
            ))}
          </ul>
        )}
      </Painel>

      <Painel titulo={`Arquivos por enviar${arquivosPendentes.length ? ` · ${arquivosPendentes.length}` : ''}`}>
        {arquivosPendentes.length === 0 ? (
          <p className="text-sm text-apagado">Todas as fotos e vídeos deste aparelho já estão no servidor.</p>
        ) : (
          <ul className="divide-y divide-borda text-sm">
            {arquivosPendentes.map(({ a, texto }) => (
              <li key={a.mediaId} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2">
                <span>{texto}</span>
                <span className={`text-xs ${a.estado === 'erro' ? 'text-erro' : 'text-apagado'}`}>
                  {megabytes(a.tamanho)} · {dataHora(a.criadoEm)}
                  {a.estado === 'erro' && a.erro && ` · ${explicarErroDeSincronia(a.erro).titulo}`}
                </span>
              </li>
            ))}
            <li className="py-2 text-xs text-apagado">
              Total: {megabytes(arquivosPendentes.reduce((t, { a }) => t + a.tamanho, 0))}. Os arquivos sobem sozinhos quando há
              internet; em rede fraca, prefira o wi-fi.
            </li>
          </ul>
        )}
      </Painel>

      <Painel titulo={`Fila de envio${pendentes ? ` · ${pendentes}` : ''}`}>
        {fila.length === 0 ? (
          <p className="text-sm text-apagado">Tudo o que foi registrado neste aparelho já está no servidor.</p>
        ) : (
          <ul className="divide-y divide-borda text-sm">
            {fila.map(({ i, texto }) => (
              <li key={i.clientMutationId} className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 py-2">
                <span>{texto}</span>
                <span className="text-xs text-apagado">
                  {dataHora(i.createdAt)}
                  {i.attempts > 0 && ` · ${i.attempts} tentativa(s): ${explicarErroDeSincronia(i.lastError ?? '').titulo}`}
                </span>
              </li>
            ))}
            {pendentes > fila.length && <li className="py-2 text-xs text-apagado">e mais {pendentes - fila.length}.</li>}
          </ul>
        )}
      </Painel>
    </div>
  );
}

function ItemQuarentena({ q, texto, online }: { q: ItemEmQuarentena; texto: string; online: boolean }) {
  const e = explicarRejeicao(q.reason);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const agir = async (acao: () => Promise<void>) => {
    setErro(null);
    setOcupado(true);
    try {
      await acao();
      if (online) await sincronizarAgora();
    } catch (x) {
      setErro(x instanceof Error ? x.message : String(x));
    } finally {
      setOcupado(false);
    }
  };

  /*
    Numero de visita repetido: a visita ganha o proximo numero livre deste
    haras e volta a fila; o que dependia dela (as fichas, recusadas por chave
    estrangeira) volta junto, na ordem original.
  */
  const renumerar = () =>
    agir(async () => {
      const visita = await db.table('visit').get(q.item.entityId);
      const doHaras = await db.table('visit').where('stud_farm_id').equals(String(visita?.stud_farm_id ?? q.item.payload.stud_farm_id)).toArray();
      const proximo = Math.max(0, ...doHaras.map((v) => Number(v.visit_number) || 0)) + 1;
      await engine.tentarDeNovo(q.id, { visit_number: proximo });
      for (const outro of await store.listQuarantine()) {
        if (explicarRejeicao(outro.reason).acao === 'tentar') await engine.tentarDeNovo(outro.id);
      }
    });

  const descartar = () => {
    const ok = window.confirm(
      'Descartar este registro? O que foi digitado neste aparelho para ele se perde, e o dado volta a ser o que está no servidor.',
    );
    if (ok) void agir(() => engine.descartar(q.id));
  };

  return (
    <li className="space-y-2 border-l-2 border-erro pl-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-medium">{texto}</p>
        <Etiqueta tom="aviso">{e.titulo}</Etiqueta>
      </div>
      <p className="text-sm text-apagado">{e.explicacao}</p>
      <p className="text-xs text-apagado">
        Registrado em {dataHora(q.item.createdAt)} · recusado em {dataHora(q.at)}
      </p>
      <details className="text-xs text-apagado">
        <summary className="cursor-pointer">Detalhe técnico</summary>
        <p className="mt-1 font-mono break-all">{q.reason}</p>
      </details>
      {erro && <p className="text-sm text-erro">{erro}</p>}
      <div className="flex flex-wrap gap-2">
        {e.acao === 'renumerar' && q.item.entity === 'visit' && (
          <Botao tamanho="sm" variante="principal" carregando={ocupado} onClick={() => void renumerar()}>
            Renumerar e reenviar
          </Botao>
        )}
        <Botao
          tamanho="sm"
          variante={e.acao === 'tentar' ? 'principal' : 'secundario'}
          carregando={ocupado}
          onClick={() => void agir(() => engine.tentarDeNovo(q.id))}
        >
          Tentar de novo
        </Botao>
        <Botao tamanho="sm" variante={e.acao === 'descartar' ? 'principal' : 'secundario'} disabled={ocupado} onClick={descartar}>
          Descartar
        </Botao>
      </div>
    </li>
  );
}

